import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, Alert, Chip, Tooltip, CircularProgress,
  FormControlLabel, InputAdornment
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const AdminManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    isSuperAdmin: false,
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/admin/admins');
      setAdmins(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nel caricamento degli admin.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        username: admin.username,
        password: '',
        isSuperAdmin: admin.isSuperAdmin,
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        username: '',
        password: '',
        isSuperAdmin: false,
      });
    }
    setShowPassword(false);
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingAdmin(null);
    setFormData({
      username: '',
      password: '',
      isSuperAdmin: false,
    });
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    try {
      if (editingAdmin) {
        // Update existing admin
        await axios.put(`/api/admin/admins/${editingAdmin.id}`, formData);
        setSuccess('Admin aggiornato con successo.');
      } else {
        // Create new admin
        if (!formData.password) {
          setError('La password è obbligatoria per creare un nuovo admin.');
          return;
        }
        await axios.post('/api/admin/admins', formData);
        setSuccess('Admin creato con successo.');
      }
      
      handleCloseDialog();
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nell\'operazione.');
    }
  };

  const handleToggleStatus = async (adminId) => {
    setError('');
    setSuccess('');
    
    try {
      await axios.patch(`/api/admin/admins/${adminId}/toggle-status`);
      setSuccess('Stato admin modificato con successo.');
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nella modifica dello stato.');
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo admin? Questa azione non può essere annullata.')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axios.delete(`/api/admin/admins/${adminId}`);
      setSuccess('Admin eliminato con successo.');
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nell\'eliminazione.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          Gestione Admin
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuovo Admin
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell>Stato</TableCell>
              <TableCell>Data Creazione</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nessun admin trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {admin.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {admin.isSuperAdmin ? (
                      <Chip
                        label="Super Admin"
                        color="primary"
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                    ) : (
                      <Chip label="Admin" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {admin.isActive ? (
                      <Chip label="Attivo" color="success" size="small" />
                    ) : (
                      <Chip label="Disattivato" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(admin.createdAt).toLocaleDateString('it-IT')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifica">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(admin)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={admin.isActive ? 'Disattiva' : 'Attiva'}>
                      <IconButton
                        size="small"
                        color={admin.isActive ? 'warning' : 'success'}
                        onClick={() => handleToggleStatus(admin.id)}
                      >
                        {admin.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(admin.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog per Crea/Modifica Admin */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAdmin ? 'Modifica Admin' : 'Nuovo Admin'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              fullWidth
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
            <TextField
              label={editingAdmin ? 'Nuova Password (lascia vuoto per non modificare)' : 'Password'}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingAdmin}
              helperText={editingAdmin ? 'Lascia vuoto per mantenere la password attuale' : 'Minimo 6 caratteri'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      onClick={() => setShowPassword(prev => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isSuperAdmin}
                  onChange={(e) => setFormData({ ...formData, isSuperAdmin: e.target.checked })}
                />
              }
              label="Super Admin"
            />
            <Typography variant="caption" color="text.secondary">
              I Super Admin possono gestire altri admin. Gli Admin standard hanno accesso a tutte le altre funzionalità della piattaforma.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingAdmin ? 'Aggiorna' : 'Crea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
